import React from "react";
import styled from "styled-components";
import ReactDraggable from "react-draggable";

import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";

import { FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { CloseGrayIcon } from "../../CommonUI/Icon/CloseGrayIcon";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { PropertyEditorInteger } from "../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorSelect } from "../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { ILinkingData } from "../../rule-engine-core/Rule/ILinkingData";
import { UISwitch } from "../../CommonUI/BuildlingBlocks/UISwitch";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

interface ILinkingDataDialog {
    editContext: {
        openLinkDialog: boolean;
        linkData: ILinkingData;
    };
}

export const LinkingDataDialog = (props: ILinkingDataDialog) => {
    const { editContext } = props;

    const closeLinkingData = () => {
        editContext.openLinkDialog = false;
    };

    const changePeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
        editContext.linkData.period = event.target.value as "every_day" | "once" | "several";
        StateManager.propertyChanged(editContext, "linkData");
    };

    const changeServerSettings = (newValue: boolean) => {
        editContext.linkData.serviceSetting = newValue;
        StateManager.propertyChanged(editContext, "linkData");
    };

    const changeStoreData = (newValue: boolean) => {
        editContext.linkData.storeData = newValue;
        StateManager.propertyChanged(editContext, "linkData");
    };

    return (
        <ObserveState
            listenTo={editContext}
            properties={["openLinkDialog", "linkData"]}
            control={() => {
                if (!editContext.openLinkDialog) return null;

                return (
                    <ReactDraggable handle=".LinkingData-root">
                        <LinkingDataDialogDiv>
                            <Wrapper>
                                <CloseButton onClick={closeLinkingData}>
                                    <CloseGrayIcon />
                                </CloseButton>
                                <Typography
                                    variant="h4"
                                    classes={{ root: "LinkingData-root" }}
                                >
                                    Linking Data
                                </Typography>
                                <div className="link-web-table">
                                    <Typography
                                        variant="body1"
                                        classes={{ root: "LinkingData-label" }}
                                    >
                                        Link on web table
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        classes={{ root: "LinkingData-value" }}
                                    >
                                        https://webtable.com
                                    </Typography>
                                </div>
                                <div className="fetch-data">
                                    <Typography
                                        variant="body1"
                                        classes={{ root: "LinkingData-label" }}
                                    >
                                        How often to fetch data?
                                    </Typography>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-label="Period"
                                            name="period"
                                            value={editContext.linkData.period}
                                            onChange={changePeriod}
                                        >
                                            <FormControlLabel
                                                value="every_day"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Every Day"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value="once"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Once every"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value="several"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Several times"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                    {editContext.linkData.period === "several" && (
                                        <SeveralPeriodData>
                                            <div className="several-data-row">
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        Times
                                                    </Typography>
                                                    <PropertyEditorInteger
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="times"
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        Period
                                                    </Typography>
                                                    <PropertyEditorSelect
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="period"
                                                        options={["day", "week", "month"]}
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="several-data-row">
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        On
                                                    </Typography>
                                                    <PropertyEditorSelect
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="onWeekDays"
                                                        options={[
                                                            "Monday",
                                                            "Tuesday",
                                                            "Wednesday",
                                                            "Thursday",
                                                            "Friday",
                                                            "Saturday",
                                                            "Sunday",
                                                        ]}
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </SeveralPeriodData>
                                    )}
                                    {editContext.linkData.period === "once" && (
                                        <SeveralPeriodData>
                                            <div className="several-data-row">
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        Value
                                                    </Typography>
                                                    <PropertyEditorInteger
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="value"
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        Period
                                                    </Typography>
                                                    <PropertyEditorSelect
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="period"
                                                        options={["day", "week", "month"]}
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="several-data-row">
                                                <div className="several-data-column">
                                                    <Typography classes={{ root: "several-data-row-label" }}>
                                                        At
                                                    </Typography>
                                                    <PropertyEditorSelect
                                                        editObject={editContext.linkData.periodData}
                                                        propertyName="at"
                                                        options={[
                                                            "1",
                                                            "2",
                                                            "3",
                                                            "4",
                                                            "5",
                                                            "6",
                                                            "7",
                                                            "8",
                                                            "9",
                                                            "10",
                                                            "11",
                                                            "12",
                                                            "13",
                                                            "14",
                                                            "15",
                                                            "16",
                                                            "17",
                                                            "18",
                                                            "19",
                                                            "20",
                                                            "21",
                                                            "22",
                                                            "23",
                                                            "24",
                                                            "25",
                                                            "26",
                                                            "27",
                                                            "28",
                                                            "29",
                                                            "30",
                                                        ]}
                                                        onChange={() => {
                                                            StateManager.propertyChanged(editContext, "linkData");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </SeveralPeriodData>
                                    )}
                                </div>
                                <div className="store-data">
                                    <Typography classes={{ root: "several-data-row-label" }}>
                                        Store data results for audit
                                    </Typography>
                                    <UISwitch
                                        value={editContext.linkData.storeData}
                                        onChange={changeStoreData}
                                    />
                                </div>
                                <div className="settings-data">
                                    <Typography classes={{ root: "several-data-row-label" }}>
                                        Service settings
                                    </Typography>
                                    <UISwitch
                                        value={editContext.linkData.serviceSetting}
                                        onChange={changeServerSettings}
                                    />
                                </div>
                                <div className="format-data">
                                    <Typography
                                        classes={{ root: "several-data-row-label" }}
                                        variant="subtitle1"
                                    >
                                        Format
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        classes={{ root: "several-data-row-description" }}
                                    >
                                        Example structure table for import in <span>CSV</span> or <span>JSON</span>{" "}
                                        format.
                                    </Typography>
                                    <FormControl
                                        component="fieldset"
                                        classes={{ root: "LinkingData-format" }}
                                    >
                                        <RadioGroup
                                            aria-label="Period"
                                            name="period"
                                            value={editContext.linkData.period}
                                            onChange={changePeriod}
                                        >
                                            <FormControlLabel
                                                value="csv"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="CSV"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value="json"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="JSON"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Wrapper>
                        </LinkingDataDialogDiv>
                    </ReactDraggable>
                );
            }}
        />
    );
};

const LinkingDataDialogDiv = styled.div`
    position: absolute;
    background: ${UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    box-sizing: border-box;
    box-shadow: 0px 6px 8px -2px ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    border-radius: 8px;
`;

const Wrapper = styled.div`
  position: relative;
  padding: 32px;
  min-width: 344px;

  .LinkingData-root {
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: center;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .LinkingData-label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .LinkingData-value {
    color: ${UIStyleConstants.BLUE_COLOR};

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
  }

  .item-row {
    margin-bottom: 16px;
  }

  .CellSetting-radioLabel .MuiTypography-body1 {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .several-data-row {
  }

  .several-data-row-label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .settings-data,
  .store-data {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
  }

  .format-data {
    border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};

    .several-data-row-label {
      margin-bottom: 2px;
    }

    .several-data-row-description {
      margin-bottom: 16px;

      font-size: 10px;
      font-style: normal;
      font-weight: 500;
      line-height: 14px;
      letter-spacing: 0em;
      text-align: left;

      color ${UIStyleConstants.GRAY_COLOR};;

      span {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
      }
    }
  }

  .LinkingData-format {
    width: 100%;

    .MuiFormGroup-root {
      flex-direction: row;
    }
  }

  .link-web-table,
  .fetch-data {
    margin-bottom: 16px;
  }

  .format-data {
    padding-top: 16px;
  }

  .several-data-row {
    display: flex;
    flex-direction: row;
  }
`;

const CloseButton = styled.div`
    position: absolute;
    top: 4px;
    right: 4px;
`;

const SeveralPeriodData = styled.div`
    display: flex;
    flex-direction: column;

    padding-left: 32px;
`;
